import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2, type LucideIcon } from 'lucide-react';
import { Slot as SlotPrimitive } from 'radix-ui';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center border-0 text-sm transition-colors duration-150 focus-visible:bg-black/4 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:focus-visible:bg-white/4',
  {
    variants: {
      variant: {
        'default':
          'bg-neutral-900 text-ui-base hover:bg-neutral-900/90 dark:bg-ui-base dark:text-neutral-900 dark:hover:bg-ui-base/90',
        'destructive':
          'bg-status-failure text-text-inverted hover:bg-status-failure-hover focus-visible:bg-status-failure-hover dark:bg-status-failure dark:text-text-inverted dark:hover:bg-status-failure-hover dark:focus-visible:bg-status-failure-hover',
        'info':
          'bg-highlight-base text-text-inverted hover:bg-highlight-hover-color dark:bg-highlight-base dark:text-text-inverted dark:hover:bg-highlight-hover-color',
        'warning':
          'bg-status-warning text-text-inverted hover:bg-status-warning-hover dark:bg-status-warning dark:text-text-inverted dark:hover:bg-status-warning-hover dark:hover:bg-status-warning-hover',
        'outline-solid':
          'border border-neutral-200 bg-transparent hover:bg-neutral-100 hover:text-neutral-900 dark:border-neutral-800 dark:text-ui-base dark:hover:bg-neutral-800 dark:hover:text-ui-base',
        'secondary': 'bg-ui-light text-black hover:bg-ui-base dark:text-white dark:focus-visible:bg-ui-base',
        'ghost':
          'hover:bg-neutral-100 hover:text-neutral-900 dark:text-ui-base dark:hover:bg-neutral-800 dark:hover:text-ui-base',
        'main-toolbar':
          'rounded-md text-icon-base hover:text-gray-icon-hover-color [&>svg]:size-6 [&>svg]:min-h-6 [&>svg]:min-w-6 [&>svg]:stroke-icon-base [&>svg]:duration-150 hover:[&>svg]:stroke-gray-icon-hover-color',
        'link': 'text-neutral-900 underline-offset-4 hover:underline dark:text-ui-base',
        'success':
          'bg-status-success text-text-inverted hover:bg-status-success-hover focus-visible:bg-status-success-hover dark:bg-status-success dark:text-text-inverted dark:hover:bg-status-success-hover dark:focus-visible:bg-status-success-hover',
        'secondary-toolbar':
          'rounded-md bg-transparent p-0 font-medium text-icon-base hover:bg-highlight-hover-color [&>svg]:size-6 [&>svg]:min-h-6 [&>svg]:min-w-6',
        'navigation-action-primary':
          'gap-x-2 rounded-xl bg-highlight-base font-bold text-text-base hover:bg-highlight-hover-color focus-visible:bg-highlight-base focus-visible:ring-2 focus-visible:ring-highlight-hover-color focus-visible:ring-offset-2 focus-visible:ring-offset-transparent dark:text-text-inverted dark:focus-visible:bg-highlight-base dark:focus-visible:ring-2',
        'navigation-action-secondary':
          'gap-x-2 rounded-xl bg-ui-bevel font-bold text-text-gray focus-within:bg-black/4 focus-visible:ring-2 focus-visible:ring-ui-bevel focus-visible:ring-offset-2 focus-visible:ring-offset-transparent dark:focus-within:bg-white/4 dark:focus-visible:ring-2',
        'search-action-primary':
          'h-12 bg-highlight-base font-bold text-text-base hover:bg-highlight-hover-color dark:text-text-inverted',
        'search-action-secondary': 'h-12 bg-ui-light font-bold text-text-gray hover:bg-ui-base',
        'selection-bar': 'rounded-xl bg-highlight-medium font-bold text-text-inverted hover:bg-highlight-hover-color',
      },
      size: {
        'default': 'min-h-9 px-4 py-2',
        'sm': 'h-8 px-3 text-xs',
        'lg': 'h-10 px-6 py-2',
        'icon': 'icon-9',
        'main-toolbar': 'size-7 min-h-7 min-w-7',
        'secondary-toolbar': 'size-6 min-h-6 min-w-6 p-4',
        'navigation-action': 'min-h-8 px-4 py-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconClassName?: string;
  /**
   * When true, forces `disabled`, swaps the `icon` for a spinning `Loader2`, and
   * suppresses `onClick`. Callers should wire this to their mutation's `isPending`
   * so an in-flight submit can't fire twice.
   */
  loading?: boolean;
  tooltip?: React.ReactNode;
  tooltipClassName?: string;
  tooltipDelayDuration?: number;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      iconClassName,
      variant,
      size,
      asChild = false,
      icon: IconProp,
      iconPosition = 'left',
      children,
      loading = false,
      disabled,
      onClick,
      tooltip,
      tooltipClassName,
      tooltipDelayDuration,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? SlotPrimitive.Slot : 'button';
    const Icon = loading ? Loader2 : IconProp;
    const resolvedDisabled = disabled || loading;
    const resolvedOnClick = loading ? undefined : onClick;

    // When a string tooltip is given and the caller supplied neither aria-label nor title,
    // use the tooltip text as the accessible name so icon-only buttons are labelled.
    const derivedAriaLabel =
      props['aria-label'] === undefined && props.title === undefined && typeof tooltip === 'string'
        ? tooltip
        : props['aria-label'];

    const iconElement = Icon && <Icon className={cn('icon-6', loading && 'animate-spin', iconClassName)} />;

    const buttonElement =
      asChild || !Icon ? (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          aria-label={derivedAriaLabel}
          {...(resolvedDisabled && { 'aria-disabled': true })}
          {...props}
          onClick={resolvedOnClick}
          disabled={resolvedDisabled}
        >
          {children}
        </Comp>
      ) : (
        <Comp
          className={cn(buttonVariants({ variant, size }), Boolean(Icon) && children && 'gap-x-2', className)}
          ref={ref}
          aria-label={derivedAriaLabel}
          {...(resolvedDisabled && { 'aria-disabled': true })}
          {...props}
          disabled={resolvedDisabled}
          onClick={resolvedOnClick}
        >
          {iconPosition === 'left' && iconElement}
          {children}
          {iconPosition === 'right' && iconElement}
        </Comp>
      );

    if (!tooltip) {
      return buttonElement;
    }

    // A native `disabled` button doesn't fire hover/focus events, so a tooltip placed directly
    // on it could never open. Wrap the (natively disabled) button in a focusable span that acts
    // as the tooltip trigger: `disabled:pointer-events-none` (from buttonVariants) lets the hover
    // fall through to the wrapper, so the button keeps its native `disabled` attribute — which
    // automated tests can assert on — while the tooltip still opens on hover/focus.
    // Presentational wrapper only — the button keeps all props (aria-*, data-cy, handlers);
    // this span just needs to be focusable so the tooltip opens while the button is disabled.
    const trigger = resolvedDisabled ? (
      <span tabIndex={0} className="inline-flex">
        {buttonElement}
      </span>
    ) : (
      buttonElement
    );

    return (
      <Tooltip delayDuration={tooltipDelayDuration}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className={tooltipClassName}>{tooltip}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
